import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'danger' | 'dangerGhost' | 'ghost';
export type ButtonSize = 'sm' | 'md';

/**
 * Cores por variante.
 *
 * O laranja institucional (#f85808, o 500) não entra como fundo de botão: texto branco sobre
 * ele dá ~3,4:1 de contraste, abaixo dos 4,5:1 exigidos. O 700 passa com folga e continua
 * sendo laranja. O tom vivo fica para o que não leva texto em cima (faixa do topo, marcadores).
 */
const VARIANTS: Record<ButtonVariant, string> = {
  // Ação principal: azul institucional oficial (#000078).
  primary: 'bg-niteroi-700 text-white shadow-sm hover:bg-niteroi-800 active:bg-niteroi-900',
  secondary:
    'border border-slate-300 bg-white text-slate-800 shadow-sm hover:border-slate-400 hover:bg-slate-50 active:bg-slate-100',
  accent: 'bg-laranja-700 text-white shadow-sm hover:bg-laranja-800 active:bg-laranja-900',
  // Vermelho cheio: só para confirmar uma ação que não tem volta (dentro de um diálogo).
  danger: 'bg-red-700 text-white shadow-sm hover:bg-red-800 active:bg-red-900',
  // Vermelho discreto: a ação perigosa numa linha de lista, sem gritar mais que as outras.
  dangerGhost: 'text-red-700 hover:bg-red-50 active:bg-red-100',
  ghost: 'text-slate-700 hover:bg-slate-100 active:bg-slate-200',
};

const DISABLED: Record<ButtonVariant, string> = {
  primary: 'disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  secondary:
    'disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:shadow-none',
  accent: 'disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  danger: 'disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  dangerGhost: 'disabled:bg-transparent disabled:text-slate-400',
  ghost: 'disabled:bg-transparent disabled:text-slate-400',
};

// Altura mínima (não fixa): rótulo longo no celular quebra em duas linhas em vez de vazar.
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-9 gap-1.5 px-3 py-1.5',
  md: 'min-h-10 gap-2 px-4 py-2',
};

interface ButtonClassesOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Enquanto carrega o botão fica travado, mas mantém a cor (não vira "desabilitado"). */
  loading?: boolean;
  className?: string;
}

/**
 * Classes de botão — usadas pelo `Button` e por links que precisam parecer botão
 * (ex.: "Ver no eCIGA", que é um `<a>` porque leva para outro site).
 */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
}: ButtonClassesOptions = {}): string {
  return cn(
    'inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed',
    SIZES[size],
    VARIANTS[variant],
    loading ? 'cursor-progress opacity-80' : DISABLED[variant],
    className,
  );
}
